import { useEffect, useState } from 'react';
import { Check, Edit3, Layers3, LoaderCircle, MousePointer2, Plus, Save, Trash2, X } from 'lucide-react';
import { createGalleryPreview } from '../core/gallery';
import { projectFromDocument, serializeProjectDocument, type YxpdDocument } from '../core/projectFile';
import { watermarkFromDocument } from '../core/watermark';
import type { PatternProject, PatternWatermark, UserWatermarkSummary } from '../types';

interface WatermarkDetail extends UserWatermarkSummary {
  document: YxpdDocument;
}

interface Props {
  project: PatternProject;
  value: PatternWatermark | null;
  onChange: (value: PatternWatermark | null) => void;
  onEdit: (project: PatternProject) => void;
  onClose: () => void;
}

async function api<T>(url: string, options?: RequestInit) {
  const response = await fetch(url, {
    ...options,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(options?.headers || {}) }
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.message || '水印图纸操作失败');
  return data as T;
}

export function WatermarkModal({ project, value, onChange, onEdit, onClose }: Props) {
  const [items, setItems] = useState<UserWatermarkSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    void api<{ watermarks: UserWatermarkSummary[] }>('/api/watermarks')
      .then((data) => setItems(data.watermarks || []))
      .catch((reason) => setMessage(reason.message))
      .finally(() => setLoading(false));
  }, []);

  async function useWatermark(item: UserWatermarkSummary) {
    setBusy(item.id);
    setMessage('');
    try {
      const data = await api<{ watermark: WatermarkDetail }>(`/api/watermarks/${encodeURIComponent(item.id)}`);
      onChange(watermarkFromDocument(item.id, item.name, data.watermark.document, value?.id === item.id ? value.x : 2, value?.id === item.id ? value.y : 2, value?.id === item.id ? value.scale : 1, false));
      setMessage(`已使用“${item.name}”，去画布拖动位置或右下角控制点调整大小，完成后点击“完成调整”`);
    } catch (reason) {
      setMessage(reason instanceof Error ? reason.message : '水印读取失败');
    } finally { setBusy(''); }
  }

  async function saveCurrentProject() {
    setBusy('save-current');
    setMessage('');
    try {
      const previewData = await createGalleryPreview(project);
      const payload = { name: project.name, previewData, document: serializeProjectDocument(project) };
      const url = project.watermarkSourceId ? `/api/watermarks/${encodeURIComponent(project.watermarkSourceId)}` : '/api/watermarks';
      const data = await api<{ watermark: WatermarkDetail }>(url, { method: project.watermarkSourceId ? 'PUT' : 'POST', body: JSON.stringify(payload) });
      setItems((current) => [data.watermark, ...current.filter((item) => item.id !== data.watermark.id)]);
      onChange(watermarkFromDocument(data.watermark.id, data.watermark.name, data.watermark.document, value?.id === data.watermark.id ? value.x : 2, value?.id === data.watermark.id ? value.y : 2, value?.id === data.watermark.id ? value.scale : 1, false));
      setMessage(project.watermarkSourceId ? '水印图纸修改已保存，并已同步当前水印层' : '当前图纸已保存到水印库并加入水印层');
    } catch (reason) {
      setMessage(reason instanceof Error ? reason.message : '水印保存失败');
    } finally { setBusy(''); }
  }

  async function editWatermark(item: UserWatermarkSummary) {
    setBusy(`edit-${item.id}`);
    setMessage('');
    try {
      const data = await api<{ watermark: WatermarkDetail }>(`/api/watermarks/${encodeURIComponent(item.id)}`);
      const next = projectFromDocument(data.watermark.document, project.ownerKey, '账号水印图纸');
      next.name = item.name;
      next.watermarkSourceId = item.id;
      onEdit(next);
      onClose();
    } catch (reason) {
      setMessage(reason instanceof Error ? reason.message : '水印图纸打开失败');
    } finally { setBusy(''); }
  }

  async function removeWatermark(item: UserWatermarkSummary) {
    if (!window.confirm(`删除水印图纸“${item.name}”？删除后无法恢复。`)) return;
    setBusy(`delete-${item.id}`);
    setMessage('');
    try {
      await api(`/api/watermarks/${encodeURIComponent(item.id)}`, { method: 'DELETE' });
      setItems((current) => current.filter((entry) => entry.id !== item.id));
      if (value?.id === item.id) onChange(null);
      setMessage('水印图纸已删除');
    } catch (reason) {
      setMessage(reason instanceof Error ? reason.message : '水印删除失败');
    } finally { setBusy(''); }
  }

  return <div className="modal-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
    <section className="watermark-modal watermark-library-modal" role="dialog" aria-modal="true" aria-labelledby="watermark-title">
      <button className="modal-close icon-button" type="button" onClick={onClose} aria-label="关闭"><X size={18} /></button>
      <p className="eyebrow">ACCOUNT WATERMARK LIBRARY</p>
      <h2 id="watermark-title">我的水印图纸</h2>
      <p>水印是与账号绑定的独立图纸，保留原本的豆色和形状，不需要审核。每个账号最多保存 5 个。</p>

      <div className="watermark-current-project">
        <span><Layers3 size={20} /><i><strong>{project.watermarkSourceId ? '正在修改水印图纸' : '将本图纸转为水印'}</strong><small>{project.name} · {project.width} × {project.height}</small></i></span>
        <button className="primary-button" type="button" disabled={Boolean(busy) || (!project.watermarkSourceId && items.length >= 5)} onClick={() => void saveCurrentProject()}>
          {busy === 'save-current' ? <LoaderCircle className="spin" size={17} /> : project.watermarkSourceId ? <Save size={17} /> : <Plus size={17} />}
          {project.watermarkSourceId ? '保存当前修改' : '加入水印库'}
        </button>
      </div>

      <div className="watermark-drag-tip"><MousePointer2 size={17} /><span><strong>位置与大小都在画布上直接调整</strong><small>按住水印拖动位置；拖右下角方块缩放，允许移到图纸边界之外。调整好后点击顶部“完成调整”，水印就不会继续跟随鼠标。</small></span></div>

      {message && <p className="watermark-message" role="status">{message}</p>}
      {loading ? <div className="watermark-library-state"><LoaderCircle className="spin" size={22} />正在读取水印库…</div>
        : <div className="watermark-library-grid">
          {items.map((item) => <article className={value?.id === item.id ? 'active' : ''} key={item.id}>
            <button className="watermark-card-main" type="button" onClick={() => void useWatermark(item)} disabled={Boolean(busy)}>
              <span><img src={item.previewData} alt={`${item.name}水印预览`} />{value?.id === item.id && <b><Check size={13} />{value.locked ? '已应用' : '调整中'}</b>}</span>
              <strong>{item.name}</strong>
              <small>{item.width} × {item.height} · {item.beadCount.toLocaleString('zh-CN')} 颗 · {item.colorCount} 色</small>
            </button>
            <footer>
              <button type="button" onClick={() => void editWatermark(item)} disabled={Boolean(busy)}><Edit3 size={14} />修改</button>
              <button type="button" onClick={() => void removeWatermark(item)} disabled={Boolean(busy)}><Trash2 size={14} />删除</button>
            </footer>
          </article>)}
          {!items.length && <div className="watermark-library-state"><Layers3 size={25} /><strong>还没有水印图纸</strong><span>点击上方“加入水印库”，把当前图纸保存为第一个水印。</span></div>}
        </div>}

      <div className="watermark-actions">
        <button className="secondary-button" type="button" onClick={() => onChange(null)} disabled={!value}><Trash2 size={17} />移除当前画布水印</button>
        <button className="primary-button" type="button" onClick={onClose}>{value && !value.locked ? '去画布调整' : '关闭水印库'}</button>
      </div>
    </section>
  </div>;
}
